// frontend/src/pages/BrowseServicesPage.tsx
// Mobile-first page to browse and add services

import React, { useState } from 'react';
import { ArrowLeft, Search, Plus, Tag, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../utils/toast';
import {
  useBrowseServiceTypes,
  useServiceFilterOptions,
  useQuickAddService
} from '@/hooks/useProviderServicesQueries';
import LoadingSpinner from '@/components/LoadingSpinner';

// Type definition for browse service types with new API structure
interface BrowseServiceType {
  service_type_id: number;
  name: string;
  description?: string;
  category_id: number;
  category_name: string;
  is_added_by_provider: boolean;
  can_add: boolean;
}

const BrowseServicesPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Filter state
  const [search, setSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  // Form state for adding services
  const [priceRanges, setPriceRanges] = useState<Record<number, string>>({});
  const [serviceDescriptions, setServiceDescriptions] = useState<Record<number, string>>({});
  const [addingServices, setAddingServices] = useState<Set<number>>(new Set());
  const [expandedForms, setExpandedForms] = useState<Set<number>>(new Set());

  // Fetch data
  const { data: browseData, isLoading: isLoadingBrowse } = useBrowseServiceTypes({
    search,
    category_id: selectedCategoryId,
    page,
    limit: 20,
  });

  const { data: filterOptions } = useServiceFilterOptions();
  const quickAddMutation = useQuickAddService();

  const serviceTypes: BrowseServiceType[] = browseData?.service_types || [];
  const pagination = browseData?.pagination;
  const categories = filterOptions?.categories || [];

  const handleAddService = async (serviceTypeId: number, serviceType: BrowseServiceType) => {
    setAddingServices(prev => new Set(prev).add(serviceTypeId));

    try {
      const priceRange = priceRanges[serviceTypeId]?.trim();
      const description = serviceDescriptions[serviceTypeId]?.trim();
      await quickAddMutation.mutateAsync({
        service_type_id: serviceTypeId,
        service_name: serviceType.name, // Use the service type name directly
        price_range: priceRange || undefined,
        description: description || undefined
      });

      // Clear the fields after adding
      setPriceRanges(prev => ({ ...prev, [serviceTypeId]: '' }));
      setServiceDescriptions(prev => ({ ...prev, [serviceTypeId]: '' }));
      
      // Collapse the form
      setExpandedForms(prev => {
        const newSet = new Set(prev);
        newSet.delete(serviceTypeId);
        return newSet;
      });
      
      toast.success('Service added successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to add service');
    } finally {
      setAddingServices(prev => {
        const newSet = new Set(prev);
        newSet.delete(serviceTypeId);
        return newSet;
      });
    }
  };

  const toggleForm = (serviceTypeId: number) => {
    setExpandedForms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serviceTypeId)) {
        newSet.delete(serviceTypeId);
      } else {
        newSet.add(serviceTypeId);
      }
      return newSet;
    });
  };

  const updatePriceRange = (serviceTypeId: number, price: string) => {
    setPriceRanges(prev => ({ ...prev, [serviceTypeId]: price }));
  };

  const updateServiceDescription = (serviceTypeId: number, description: string) => {
    setServiceDescriptions(prev => ({ ...prev, [serviceTypeId]: description }));
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId ? parseInt(categoryId, 10) : undefined);
    setPage(1);
  };

  const handleSearchChange = (searchValue: string) => {
    setSearch(searchValue);
    setPage(1);
  };

  const hasActiveFilters = !!(search || selectedCategoryId);

  const clearFilters = () => {
    setSearch('');
    setSelectedCategoryId(undefined);
    setPage(1);
    // Collapse all expanded forms when clearing filters
    setExpandedForms(new Set());
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/provider-services')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
                Browse Services
              </h1>
              <p className="text-sm text-gray-600 sm:text-base">
                Add services to your offerings
              </p>
            </div>
          </div>

          {/* Mobile Action Bar */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <Filter size={16} />
              Filters
              {hasActiveFilters && (
                <span className="bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">
                  {[search, selectedCategoryId].filter(Boolean).length}
                </span>
              )}
            </button>
            
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="mb-6 p-4 bg-white rounded-lg shadow-sm">
            <div className="space-y-4">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategoryId || ''}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search service types..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm">
          {isLoadingBrowse ? (
            <div className="p-6">
              <LoadingSpinner message="Loading service types..." />
            </div>
          ) : serviceTypes.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-gray-500 mb-2">
                {hasActiveFilters ? 'No service types found matching your filters' : 'No service types available'}
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  Clear filters to see all service types
                </button>
              )}
            </div>
          ) : (
            <div>
              {/* Service Types List */}
              <div className="divide-y divide-gray-200">
                {serviceTypes.map((serviceType) => (
                  <div key={serviceType.service_type_id} className="p-4 sm:p-6">
                    <div className="space-y-4">
                      {/* Service Type Header */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-base sm:text-lg">
                            {serviceType.name}
                          </h3>
                          
                          {serviceType.category_name && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                <Tag size={10} />
                                {serviceType.category_name}
                              </span>
                            </div>
                          )}
                          
                          {serviceType.description && (
                            <p className="text-gray-600 text-sm mt-2 leading-relaxed">
                              {serviceType.description}
                            </p>
                          )}
                        </div>

                        {/* Availability Status */}
                        <div className="flex-shrink-0">
                          {serviceType.can_add ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Available
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Already Added
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Add Service Section - Only show if available */}
                      {serviceType.can_add && (
                        <div className="pt-3 border-t border-gray-100">
                          {!expandedForms.has(serviceType.service_type_id) ? (
                            /* Collapsed State - Show Add Button */
                            <div className="flex justify-end">
                              <button
                                onClick={() => toggleForm(serviceType.service_type_id)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                              >
                                <Plus size={16} />
                                Add Service
                              </button>
                            </div>
                          ) : (
                            /* Expanded State - Show Form */
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-900">
                                  Adding: {serviceType.name}
                                </h4>
                                <button
                                  onClick={() => toggleForm(serviceType.service_type_id)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  ✕
                                </button>
                              </div>

                              <div className="space-y-3">
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                  {/* Price Range Input */}
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Price Range (Optional)
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="e.g., $50-100"
                                      value={priceRanges[serviceType.service_type_id] || ''}
                                      onChange={(e) => updatePriceRange(serviceType.service_type_id, e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                      disabled={addingServices.has(serviceType.service_type_id)}
                                    />
                                  </div>
                                </div>

                                {/* Custom Description Input */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Your Service Description (Optional)
                                  </label>
                                  <textarea
                                    rows={3}
                                    placeholder="Customize the description for your specific service offering..."
                                    value={serviceDescriptions[serviceType.service_type_id] || ''}
                                    onChange={(e) => updateServiceDescription(serviceType.service_type_id, e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                    disabled={addingServices.has(serviceType.service_type_id)}
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    Leave blank to use the default description. Add your own to stand out.
                                  </p>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => toggleForm(serviceType.service_type_id)}
                                  disabled={addingServices.has(serviceType.service_type_id)}
                                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleAddService(serviceType.service_type_id, serviceType)}
                                  disabled={addingServices.has(serviceType.service_type_id)}
                                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                >
                                  {addingServices.has(serviceType.service_type_id) ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                      Adding...
                                    </>
                                  ) : (
                                    <>
                                      <Plus size={16} />
                                      Add Service
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination && pagination.total_pages > 1 && (
                <div className="p-4 border-t">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-sm text-gray-700 text-center sm:text-left">
                      Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                      {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                      {pagination.total} service types
                    </div>
                    
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      <span className="px-3 py-2 text-sm text-gray-700">
                        {pagination.current_page} / {pagination.total_pages}
                      </span>
                      
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={page === pagination.total_pages}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowseServicesPage;